import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { zoneService } from '@/services/zoneService';
import { STATUT_ZONE_COLORS, TYPE_ENGIN_COLORS, TYPE_ENGIN_LABELS } from '@/types';
import { clsx } from 'clsx';
import {
  CheckCircleIcon,
  ClockIcon,
  MapIcon,
} from '@heroicons/react/24/solid';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

interface ZoneProgressCardProps {
  deneignementId: number;
  showLink?: boolean;
}

/**
 * Carte compacte affichant la progression des zones
 * Pour la page de detail d'une intervention
 */
export function ZoneProgressCard({ deneignementId, showLink = true }: ZoneProgressCardProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['deneignement-zones', deneignementId],
    queryFn: () => zoneService.listDeneignementZones(deneignementId),
    refetchInterval: 60000, // Rafraichir toutes les minutes
  });

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Progression zones</h3>
        </div>
        <div className="card-body">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-8 bg-gray-200 rounded w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { zones, progression, stats } = data;

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h3 className="text-lg font-semibold">Progression zones</h3>
        <span className="text-2xl font-bold text-primary-600">{progression.toFixed(0)}%</span>
      </div>
      <div className="card-body space-y-4">
        {/* Barre de progression */}
        <div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progression}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              {stats.termine} terminees
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              {stats.en_cours} en cours
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              {stats.a_faire} a faire
            </span>
          </div>
        </div>

        {/* Liste compacte des zones */}
        <div className="space-y-2">
          {zones.map((dz) => (
            <div
              key={dz.id}
              className={clsx(
                'flex items-center gap-3 p-2 rounded-lg',
                dz.statut === 'termine' && 'bg-green-50',
                dz.statut === 'en_cours' && 'bg-blue-50',
                dz.statut === 'a_faire' && 'bg-gray-50'
              )}
            >
              {/* Icone statut */}
              {dz.statut === 'termine' ? (
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
              ) : dz.statut === 'en_cours' ? (
                <ClockIcon className="w-5 h-5 text-blue-500 animate-pulse" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
              )}

              {/* Info zone */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">Z{dz.zone.numero}</span>
                  <span className="text-xs text-gray-500 truncate">{dz.zone.nom}</span>
                </div>
              </div>

              {/* Badge type engin */}
              {dz.zone.typeEnginRequis && (
                <span
                  className={clsx(
                    'px-1.5 py-0.5 text-xs font-medium rounded text-white',
                    TYPE_ENGIN_COLORS[dz.zone.typeEnginRequis]
                  )}
                >
                  {TYPE_ENGIN_LABELS[dz.zone.typeEnginRequis].charAt(0)}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Lien vers la page zones */}
        {showLink && (
          <Link
            to={`/deneignements/${deneignementId}/zones`}
            className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors font-medium text-sm"
          >
            <MapIcon className="w-4 h-4" />
            <span>Gerer les zones</span>
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

export default ZoneProgressCard;
